import React, { CSSProperties, ReactNode, useMemo } from 'react';
import { Col, Row } from '../../../antd/rowcol';
import classNames from 'classnames';
import { HorizontalSpacer } from '../../layout/horizontalspacer';
import { PaddingType, Panel } from '../../layout/panel';

/**
 * {@docCategory Card}
 */
export interface CardProps {
   /**
    * Card icon
    */
   icon?: ReactNode;

   /**
    * Card title
    */
   title?: ReactNode;

   /**
    * Toggles rendering of the border around the card
    */
   border?: boolean;

   /**
    * Shows a loading indicator while the contents of the card are being fetched
    */
   loading?: boolean;

   /**
    * Content to render in the top-right corner of the card
    */
   extra?: ReactNode;

   /**
    * The card size.
    * @defaultvalue default
    */
   size?: 'default' | 'small';

   /**
    * The action list, shows at the bottom of the Card
    */
   actions?: Array<ReactNode>;

   /**
    * Determines if the panel will fill its parent's width and height.
    *
    * @defaultvalue false
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill its parent's width.
    *
    * @defaultvalue false
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill its parent's height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * Set the className.
    */
   className?: string;

   /**
    * Set styles.
    */
   style?: CSSProperties;

   /**
    * The width of the card. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    */
   width?: string | number;

   /**
    * The height of the card. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    */
   height?: string | number;

   /**
    * The minimum width of the card. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the card. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    */
   minHeight?: string | number;

   /**
    * The maximum width of the card. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the card. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    */
   maxHeight?: string | number;

   /**
    * Set className for the header.
    */
   headerClassName?: string;

   /**
    * Set styles for the header.
    */
   headerStyle?: CSSProperties;

   /**
    * Set className for the content.
    */
   contentClassName?: string;

   /**
    * Set styles for the content.
    */
   contentStyle?: CSSProperties;

   /**
    * Determines if the card has padding
    *
    * @defaultvalue true
    */
   padding?: PaddingType;

   /**
    * Set className for the action bar.
    */
   actionBarClassName?: string;

   /**
    * Set styles for the action bar.
    */
   actionBarStyle?: CSSProperties;

   /**
    * onClick callback for when the user clicks inside the card
    */
   onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const Card = (props: React.PropsWithChildren<CardProps>) => {
   const bordered = props.border === undefined ? true : props.border;
   const classString = classNames(
      'uf-card',
      {
         ['uf-card-loading']: props.loading,
         ['uf-card-bordered']: bordered,
         ['uf-card-small']: props.size,
         ['uf-card-vertical-fill']: props.fill || props.verticalFill
      },
      props.className,
   );

   const width = props.fill || props.horizontalFill ? props.width : props.width || 'auto';
   const height = props.fill || props.verticalFill ? props.height : props.height || 'auto';

   const header = useMemo(() => (
         (props.icon !== undefined || props.title !== undefined || props.extra !== undefined) && <Header title={props.title} icon={props.icon} extra={props.extra} headerClassName={props.headerClassName} headerStyle={props.headerStyle}/>
      ),[props.icon, props.title, props.extra, props.headerClassName, props.headerStyle]);

   const footer = useMemo(()=>(
      actions(props.actions, props.actionBarStyle, props.actionBarClassName)
                          ),
      [props.actions, props.actionBarClassName, props.actionBarStyle]);

   return (
         <Panel
            className={classString}
            style={props.style}
            width={width}
            height={height}
            minWidth={props.minWidth}
            minHeight={props.minHeight}
            maxWidth={props.maxWidth}
            maxHeight={props.maxHeight}
            fill={props.fill}
            verticalFill={props.verticalFill}
            horizontalFill={props.horizontalFill ? props.horizontalFill : (props.fill ? props.fill : false)}
            onClick={props.onClick}
         >
            {header}
            {body(props.loading,
                  props.contentClassName,
                  props.contentStyle,
                  props.padding,
                  props.children)}
            {footer}
         </Panel>
   );
};

interface CardHeaderProps {
   icon?: ReactNode;
   title?: ReactNode;
   extra?: ReactNode;
   headerClassName?: string;
   headerStyle?: CSSProperties;
}

const Header = (props: CardHeaderProps) => {
   const icon = props.icon;
   const title = props.title;
   const extra = props.extra;
   const headerClassName = props.headerClassName ? `${props.headerClassName || ''}` : '';

   return (
      <div className={`uf-card-head ${headerClassName || ''}`} style={props.headerStyle}>
         <div className={`uf-card-head-wrapper`}>
            {(icon && title) && <div className={`uf-card-head-title`}>{<>{icon}<HorizontalSpacer/>{title}</>}</div>}
            {(icon && !title) && <div className={`uf-card-head-title`}>{icon}</div>}
            {(!icon && title) && <div className={`uf-card-head-title`}>{title}</div>}
            {extra && <div className={`uf-card-extra`}>{extra}</div>}
         </div>
      </div>
   );
};

const body = (loading?: boolean, contentClassName?: string, contentStyle?: CSSProperties, padding?: PaddingType, children?: ReactNode) => {
   return (
      <div className={`uf-card-body ${contentClassName || ''} ${getPaddingClassName(padding !== undefined ? padding : true)}`} style={contentStyle}>
         {loading ? loadingBlock(contentStyle) : children}
      </div>
   );
};

const getPaddingClassName = (paddingProp?: PaddingType) => {
   if (paddingProp === true) {
      return ` uf-card-body-padding`;
   }
   else if (paddingProp) {
      return ` uf-card-body-padding-${paddingProp}`;
   }
   else if (paddingProp === false) {
      return ` uf-card-body-padding-none`;
   }
   return '';
};

const actions = (actions?: Array<ReactNode>, actionBarStyle?: CSSProperties, actionBarClassName?: string) => {

   return actions && actions.length
          ? (
             <ul
                className={`uf-card-actions ${actionBarClassName || ''}`}
                style={actionBarStyle}
             >
                {getAction(actions)}
             </ul>
          )
          : undefined;
};

const loadingBlock = (contentStyleProp?: CSSProperties) => {
   const contentStyle = contentStyleProp || {};
   const loadingBlockStyle = contentStyle.padding === 0 || contentStyle.padding === '0px' ? { padding: 24 } : undefined;
   const block = <div className={`uf-card-loading-block`}/>;

   return (
      <div className={`uf-card-loading-content`} style={loadingBlockStyle}>
         <Row gutter={8}>
            <Col span={22}>{block}</Col>
         </Row>
         <Row gutter={8}>
            <Col span={7}>{block}</Col>
            <Col span={15}>{block}</Col>
         </Row>
         <Row gutter={8}>
            <Col span={4}>{block}</Col>
            <Col span={18}>{block}</Col>
         </Row>
         <Row gutter={8}>
            <Col span={13}>{block}</Col>
            <Col span={9}>{block}</Col>
         </Row>
         <Row gutter={8}>
            <Col span={4}>{block}</Col>
            <Col span={2}>{block}</Col>
            <Col span={16}>{block}</Col>
         </Row>
      </div>
   );
};


const getAction = (actions: React.ReactNode[]) => {
   return actions.map((action, index) => (
      <li style={{ width: `${100 / actions.length}%` }} key={`action-${index}`}>
         <span>{action}</span>
      </li>
   ));
};
